import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { MessageSquarePlus, Bug, Lightbulb, MessageCircle, ImagePlus, X, Loader2, Send } from 'lucide-react';

// Compress a chosen image client-side so uploads stay small.
const compressImage = (file, maxDim = 1280, quality = 0.8) => new Promise((resolve) => {
  try {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => resolve(blob || file), 'image/jpeg', quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  } catch (e) { resolve(file); }
});

const TYPES = [
  { key: 'bug', icon: Bug, color: '#FF6B35' },
  { key: 'idea', icon: Lightbulb, color: '#2EE59D' },
  { key: 'general', icon: MessageCircle, color: '#00B4D8' },
];

export const FeedbackWidget = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('bug');
  const [message, setMessage] = useState('');
  const [shot, setShot] = useState(null); // { blob, previewUrl }
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!user) { setEnabled(false); return; }
    api.get('/feedback/config')
      .then(({ data }) => { if (!cancelled) setEnabled(!!data.enabled); })
      .catch((e) => { console.debug('feedback config load failed', e); });
    return () => { cancelled = true; };
  }, [user]);

  const reset = () => {
    setType('bug'); setMessage('');
    if (shot?.previewUrl) URL.revokeObjectURL(shot.previewUrl);
    setShot(null);
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error(t('feedback.imageError')); return; }
    const blob = await compressImage(file);
    if (shot?.previewUrl) URL.revokeObjectURL(shot.previewUrl);
    setShot({ blob, previewUrl: URL.createObjectURL(blob) });
  };

  const removeShot = () => {
    if (shot?.previewUrl) URL.revokeObjectURL(shot.previewUrl);
    setShot(null);
  };

  const submit = async () => {
    if (!message.trim()) { toast.error(t('feedback.emptyError')); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('type', type);
      fd.append('message', message.trim());
      fd.append('page_url', window.location.pathname + window.location.search);
      fd.append('user_agent', navigator.userAgent || '');
      if (shot?.blob) fd.append('screenshot', shot.blob, 'screenshot.jpg');
      await api.post('/feedback', fd);
      toast.success(t('feedback.success'));
      reset();
      setOpen(false);
    } catch (e) {
      console.error('feedback submit failed', e);
      toast.error(t('feedback.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!user || !enabled) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label={t('feedback.button')}
        data-testid="feedback-widget-button"
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold pl-3.5 pr-4 py-3 shadow-lg shadow-[#00B4D8]/25 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#48CAE4]/60"
      >
        <MessageSquarePlus className="w-5 h-5" />
        <span className="hidden sm:inline text-sm">{t('feedback.button')}</span>
      </button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="bg-[#12161B] border-white/10 text-white sm:max-w-md" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{t('feedback.title')}</DialogTitle>
            <DialogDescription className="text-muted-foreground">{t('feedback.subtitle')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-1">
            {/* type */}
            <div className="grid grid-cols-3 gap-2" data-testid="feedback-type-group">
              {TYPES.map((tp) => {
                const Icon = tp.icon;
                const active = type === tp.key;
                return (
                  <button
                    key={tp.key}
                    onClick={() => setType(tp.key)}
                    data-testid={`feedback-type-${tp.key}`}
                    className={`flex flex-col items-center gap-1.5 rounded-xl border px-2 py-3 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#48CAE4]/50 ${active ? 'border-white/30 bg-white/10' : 'border-white/10 bg-[#0A0A0A] hover:bg-white/5'}`}
                    style={active ? { borderColor: tp.color, boxShadow: `inset 0 0 0 1px ${tp.color}` } : undefined}
                  >
                    <Icon className="w-5 h-5" style={{ color: tp.color }} />
                    {t(`feedback.type_${tp.key}`)}
                  </button>
                );
              })}
            </div>

            {/* message */}
            <div>
              <Label className="text-sm">{t('feedback.messageLabel')}</Label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('feedback.messagePlaceholder')}
                rows={4}
                className="mt-1.5 bg-[#0A0A0A] border-white/15 resize-none"
                data-testid="feedback-message"
              />
            </div>

            {/* screenshot */}
            <div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickFile} data-testid="feedback-file-input" />
              {!shot ? (
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}
                  className="w-full border-dashed border-white/20 bg-white/5 hover:bg-white/10 text-white/80" data-testid="feedback-add-screenshot">
                  <ImagePlus className="w-4 h-4 mr-2" /> {t('feedback.screenshot')}
                </Button>
              ) : (
                <div className="relative rounded-xl overflow-hidden border border-white/10" data-testid="feedback-screenshot-preview">
                  <img src={shot.previewUrl} alt="screenshot preview" className="w-full max-h-40 object-cover" />
                  <button onClick={removeShot} aria-label={t('feedback.removeScreenshot')}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center" data-testid="feedback-remove-screenshot">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            <Button onClick={submit} disabled={submitting || !message.trim()}
              className="w-full bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl disabled:opacity-50" data-testid="feedback-submit">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {submitting ? t('feedback.sending') : t('feedback.submit')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeedbackWidget;
